/**
 * The POS redemption panel's copy and affordances — PURE. Every input arrives as an argument;
 * nothing here reads the store, and nothing here re-decides a loyalty rule.
 *
 * 🔴 **What it is NOT.** It does not work out what may be redeemed, whether this bill qualifies,
 * or what points are worth. That is `getRedemptionPanelState` / `getRedemptionLimits`, shared
 * verbatim with the kiosk. This module takes that answer and says how the POS PANEL draws it.
 *
 * It is the sibling of `kiosk/loyalty/pointsCardModel`, and the pair is the whole POS↔kiosk
 * divergence in this flow: same rules, same state, two mappings onto copy. A model that only
 * maps a state onto sentences cannot drift from the rules, because it holds none — which is the
 * property that stopped the kiosk quietly re-implementing loyalty in a smaller, subtly different
 * form (`KIOSK_ARCHITECTURE.md` §13.16).
 *
 * ## Why the view gets sentences rather than fields
 *
 * `useLoyaltyRedemption` returns twenty values and a four-way discriminated state. Rendering it
 * directly means every surface re-writes the same string interpolation, the same "is there a
 * minimum order value" test and the same "should Change amount appear" test — and web and mobile
 * had ALREADY drifted on all three by the time this was written (see the deviations below). A
 * view that receives finished copy has nothing left to disagree about, and this file carries the
 * unit tests that pin it.
 *
 * ## 🔴 Providers stay DATA
 *
 * No function here tests a `metaId`, and none may. Everything provider-specific has already been
 * decided by a rule reading `profiles/`: whether a panel appears at all, what the ceiling is,
 * what points convert at, whether items can be excluded. Adding a provider must remain "a file
 * of deltas, or nothing at all" — if a new partner needs different COPY, add a field to
 * `LoyaltyProviderProfile` and read it here, never an `if`.
 */
import { getBurnRules, groupRedemptionExclusions } from "./program";
import { displayPoints } from "./programDisplay";
import type { MoneyFormat } from "../utils/currency/format";
import type {
  LoyaltyProgram,
  ProgramExclusion,
  RedemptionLimits,
  RedemptionPanelState,
} from "./types";

/** The programme's collectible mark, for the icon beside a headline. */
export interface CollectibleMark {
  /** `collectibleImageUrl` — a URL, or one of the named marks. */
  imageUrl?: string;
  /** `collectibleName`, the last-resort monogram when there is no artwork. */
  name?: string;
}

/**
 * What the panel needs beyond the redemption state, so this file stays store-free.
 *
 * Every field is something a hook already computes — the model exists to arrange them, not to
 * fetch them.
 */
export interface RedemptionPanelContext {
  /** The channel's programme, from `getProgram(programDetails)`. */
  program?: LoyaltyProgram | null;
  /** `useCollectibleLabel()` — what this programme calls its points ("Point"/"Star"/"Bean"). */
  collectibleLabel: (count?: number, transform?: string) => string;
  /** From `currency()`. An argument for the same reason `buildRedemptionTooltip` takes one. */
  money: MoneyFormat;
  /** `buildRedemptionTooltip`'s sentence, or `""` when the programme publishes no cap. */
  tooltip: string;
}

/** One labelled affordance. A label present means the control exists; absent means it does not. */
export type PanelActionLabel = string | undefined;

interface PanelShared {
  collectible: CollectibleMark;
  /**
   * The "you can redeem up to…" sentence, or `""`.
   *
   * ⚠️ The view must render this as a TAP-to-expand disclosure, not a hover tooltip: the same
   * panel runs on a touch screen, where a `title` attribute or a mouse-over is unreachable.
   */
  tooltip: string;
}

/** Points are on the bill. */
export interface AppliedPanelModel extends PanelShared {
  kind: "applied";
  /**
   * "240 Points (₹120.00) applied" — the `₹` and the two digits are illustrative: both come from
   * the caller's `money()`, which resolves them from the business
   * (`@rista/core/utils/currency`), never a constant of this module's.
   */
  headline: string;
  /** The confirmation chip. */
  badge: string;
  /** "Total available Points: 500, up to 240 Points can be used in this bill" */
  detail: string;
  /** "Customer saved ₹120 using loyalty Points" */
  savings: string;
  /** Present only when less than the ceiling was used — there is nothing to change otherwise. */
  changeAmountLabel: PanelActionLabel;
  removeLabel: string;
}

/** The customer is blocked from redeeming points on this bill. */
export interface BlockedPanelModel {
  kind: "blocked";
  collectible: CollectibleMark;
  headline: string;
}

/** The customer has points, and this bill cannot take them. */
export interface IneligiblePanelModel extends PanelShared {
  kind: "ineligible";
  headline: string;
  /** "Total available balance: 500 Points" */
  detail: string;
  /**
   * Why this bill cannot take them, built rather than chosen — a cart can be under the minimum
   * AND hold excluded items. `""` when the provider simply quoted zero and named no cause.
   */
  reason: string;
  /** "Know more >", present only when the programme actually publishes rules to show. */
  rulesLabel: PanelActionLabel;
  /**
   * How to outline the card. Nothing in the cart is at fault under `dashed` — the bill just has
   * not reached the threshold yet, so the card reads as an invitation rather than a rejection.
   */
  tone: "dashed" | "outlined";
}

/** Points can be applied. */
export interface AvailablePanelModel extends PanelShared {
  kind: "available";
  /** "Use 240 (₹120) loyalty Points" */
  headline: string;
  /** "Total available Points: 500" */
  detail: string;
  /**
   * "Max Redeemable: 20% of total bill", or `""`.
   *
   * ⚠️ It is NOT the `tooltip`, which describes the PROGRAMME's cap and is what a
   * programme-driven partner publishes. This one is the provider's own
   * `percentageCapOnBillAmount`, quoted with the balance by the partners that have no programme
   * — so the two never appear together, and neither can stand in for the other.
   *
   * `""` when no cap is in force, which is the common case; the view renders nothing for it.
   * A string rather than a number so no surface re-writes the sentence.
   */
  capNote: string;
  openPartialLabel: string;
  applyLabel: string;
  /** The ceiling, kept beside the copy so the editor can be seeded without re-reading limits. */
  maxPoints: number;
}

/**
 * What the panel draws.
 *
 * `hidden` is not "empty": it is the state between mount and the provider's first answer, and
 * rendering nothing there is what stops the panel offering "Use 0 (₹0)" with a live Apply button.
 */
export type LoyaltyPanelModel =
  | { kind: "hidden" }
  | AppliedPanelModel
  | BlockedPanelModel
  | IneligiblePanelModel
  | AvailablePanelModel;

/**
 * Turn the shared redemption state into the POS panel.
 *
 * | `RedemptionPanelState` | panel | |
 * |---|---|---|
 * | `hidden` | `hidden` | no provider, or the balance is still in flight |
 * | `applied` | `applied` | with "Change amount" only when there is headroom |
 * | `blocked` | `blocked` | show the loyalty block status without redemption actions |
 * | `ineligible` | `ineligible` | say why, offer the rules |
 * | `available` | `available` | apply the ceiling, or open the editor |
 *
 * 🔴 **Three deliberate departures from the web original**, each because web and mobile had
 * already drifted and one of the two was wrong:
 *
 * 1. **The collectible label is used everywhere.** Web's ineligible card hard-coded the word
 *    "points" into "Min. order value to redeem points is ₹X" — under a programme that calls them
 *    Beans, that one sentence said "points" while the four around it said "Beans". Mobile had it
 *    right.
 * 2. **The ineligible card explains itself when there is no minimum order value.** Web showed the
 *    "Add more items" headline and then rendered NO reason at all, because the whole explanation
 *    sat inside `minOrderValue > 0`. A cart blocked purely by excluded items therefore said
 *    "add more items" and stopped — advice that cannot work. `blockedReason` covers all four
 *    combinations, as the kiosk's model already did.
 * 3. **"Know more" appears only when there is something to read.** Web nested it inside the
 *    minimum-order-value branch, so a programme with exclusions but no minimum could not open
 *    its own rules; and had the branch been widened naively, a programme publishing neither
 *    would have offered a link to an empty sheet.
 */
export function toRedemptionPanel(
  state: RedemptionPanelState,
  context: RedemptionPanelContext,
): LoyaltyPanelModel {
  const { collectibleLabel, money, tooltip, program } = context;
  const collectible: CollectibleMark = {
    imageUrl: program?.collectibleImageUrl,
    name: program?.collectibleName,
  };

  switch (state.kind) {
    case "applied": {
      const applied = displayPoints(state.appliedPoints);
      const total = displayPoints(state.totalPoints);
      const ceiling = state.limits.maxPoints;

      return {
        kind: "applied",
        collectible,
        tooltip,
        headline: `${applied} ${collectibleLabel(applied)} (${money(state.appliedAmount)}) applied`,
        badge: "✓ Applied",
        detail: `Total available ${collectibleLabel(total)}: ${total}, up to ${ceiling} ${collectibleLabel(
          ceiling,
        )} can be used in this bill`,
        savings: `Customer saved ${money(state.appliedAmount)} using loyalty ${collectibleLabel()}`,
        // Offered only when there is headroom to move to. `canChangeAmount` is the rule's word
        // for that, and re-deriving it from the two figures here would be a second definition.
        changeAmountLabel: state.canChangeAmount ? "Change amount" : undefined,
        removeLabel: "Remove",
      };
    }

    case "blocked": {
      return {
        kind: "blocked",
        collectible,
        headline: `Loyalty ${collectibleLabel()} cannot be used on this bill`,
      };
    }

    case "ineligible": {
      const total = displayPoints(state.totalPoints);
      const hasMinimum = state.minOrderValue > 0;

      return {
        kind: "ineligible",
        collectible,
        tooltip,
        headline: hasMinimum
          ? `Add more items to redeem loyalty ${collectibleLabel()}`
          : `Loyalty ${collectibleLabel()} cannot be used on this bill`,
        detail: `Total available balance: ${total} ${collectibleLabel(total)}`,
        reason: blockedReason(state, context),
        rulesLabel: hasReadableRules(program) ? "Know more" : undefined,
        // An excluded line is a property of THIS cart (`cartHasExcludedItems`), so the card is a
        // solid statement about it. A bare minimum-order shortfall is a threshold the next item
        // may cross.
        tone: state.cartHasExcludedItems ? "outlined" : "dashed",
      };
    }

    case "available": {
      const total = displayPoints(state.totalPoints);
      const { maxPoints, maxAmount, billCapPercentage } = state.limits;

      return {
        kind: "available",
        collectible,
        tooltip,
        headline: `Use ${maxPoints} (${money(maxAmount)}) loyalty ${collectibleLabel(maxPoints)}`,
        detail: `Total available ${collectibleLabel(total)}: ${total}`,
        // The rule already decided whether a cap applied; this only says it. A falsy percentage
        // is "no cap", never a "0% of total bill" line — which is also why the test is here and
        // not in a JSX `&&`, where a numeric 0 renders as the character "0".
        capNote: billCapPercentage ? `Max Redeemable: ${billCapPercentage}% of total bill` : "",
        openPartialLabel: `Use partial ${collectibleLabel()}`,
        applyLabel: `Apply ${collectibleLabel()}`,
        maxPoints,
      };
    }

    default:
      return { kind: "hidden" };
  }
}

/**
 * Why this bill cannot take the customer's points.
 *
 * The two causes are independent and can both hold, so the sentence is BUILT rather than chosen.
 * When neither is known — the provider simply quoted zero — it says the honest thing rather than
 * inventing a threshold, and the caller renders nothing.
 */
function blockedReason(
  { minOrderValue, cartHasExcludedItems }: Extract<RedemptionPanelState, { kind: "ineligible" }>,
  { collectibleLabel, money }: RedemptionPanelContext,
): string {
  const label = collectibleLabel();

  if (minOrderValue > 0) {
    const base = `Min. order value to redeem ${label} is ${money(minOrderValue)}.`;
    return cartHasExcludedItems ? `${base} Some items are not eligible.` : base;
  }

  return cartHasExcludedItems ? `Some items in this cart are not eligible for ${label}.` : "";
}

/**
 * Does the programme publish anything the rules sheet could actually show?
 *
 * The sheet lists a minimum order value, a burn cap or percentage, and the programme's excluded
 * entries. A programme with none of those opens an empty dialog, so the link is withheld — which
 * is a rule about the PROGRAMME, not about this bill, and so is asked of the programme.
 */
export function hasReadableRules(program?: LoyaltyProgram | null): boolean {
  if (!program) return false;
  const { burnValue, burnCap, minOrderValue } = getBurnRules(program);
  return (
    burnValue > 0 || burnCap > 0 || minOrderValue > 0 || groupRedemptionExclusions(program).hasAny
  );
}

/* ── the partial-points editor ──────────────────────────────────────────────────────────── */

/** The editor's copy. Its VALUE and validity stay with `useLoyaltyRedemption`. */
export interface PartialEditorModel {
  /** "Enter partial Points to redeem" */
  prompt: string;
  /** The unit shown against the field. */
  unit: string;
  /**
   * "Up to 240 Points can be used" — web v1 dropped "in this bill" here (`1670fc16e`) and kept it
   * in the panel's detail line, which `toRedemptionPanel` still says.
   */
  hint: string;
  /** "= ₹120 off", or `""` when the typed figure is worth nothing yet. */
  preview: string;
  applyLabel: string;
  cancelLabel: string;
}

export function toPartialEditor(
  { maxPoints }: Pick<RedemptionLimits, "maxPoints">,
  previewAmount: number,
  { collectibleLabel, money }: RedemptionPanelContext,
): PartialEditorModel {
  return {
    prompt: `Enter partial ${collectibleLabel()} to redeem`,
    unit: collectibleLabel(),
    hint: `Up to ${maxPoints} ${collectibleLabel(maxPoints)} can be used`,
    // A zero preview is not "₹0 off" — it is nothing worth saying yet, and the field is
    // usually mid-edit when it holds one.
    preview: previewAmount > 0 ? `= ${money(previewAmount)} off` : "",
    applyLabel: `Apply ${collectibleLabel()}`,
    cancelLabel: "Cancel",
  };
}

/* ── the rules sheet ("Know more") ──────────────────────────────────────────────────────── */

/** One group of things the programme will not let points pay for. */
export interface ExclusionGroup {
  title: string;
  /** "Latte, Cold Brew." — already joined, so the view holds no list formatting. */
  names: string;
}

/** "How you redeem points" — every sentence the sheet shows, already written. */
export interface RedemptionRulesModel {
  title: string;
  /** The programme's caps, in reading order. Empty when it publishes none. */
  rules: string[];
  /**
   * Present only when the PROGRAMME excludes something from redemption. The list is not filtered
   * by the cart — see `RedemptionRulesModal`'s header.
   */
  exclusions?: {
    intro: string;
    /** The first excluded item, shown before the list is expanded. */
    lead: string;
    expandLabel: string;
    groups: ExclusionGroup[];
  };
}

/**
 * The rules sheet, built from the programme.
 *
 * 🔴 **The exclusion grouping is a RULE, not a formatting step.** `groupRedemptionExclusions`
 * drops earn-scope entries, because a programme can bar an item from EARNING points without
 * barring it from being PAID FOR with them — listing those here would tell a cashier an item is
 * ineligible when it is not.
 */
export function toRedemptionRules(
  program: LoyaltyProgram | null | undefined,
  { collectibleLabel, money }: RedemptionPanelContext,
): RedemptionRulesModel {
  const label = collectibleLabel();
  const { burnValue, burnCap, minOrderValue, methodLabel, movLabel } = getBurnRules(program);
  const scope = movLabel ? ` (${movLabel})` : "";

  const rules: string[] = [];
  if (minOrderValue > 0) {
    rules.push(`Min. order value to redeem ${label} is ${money(minOrderValue)}${scope}`);
  }
  if (burnValue > 0) {
    rules.push(`You can redeem up to ${burnValue}% of ${methodLabel}${scope}`);
  }
  if (burnCap > 0) {
    rules.push(`Maximum ${money(burnCap)} can be redeemed per transaction`);
  }

  const grouped = groupRedemptionExclusions(program);

  return {
    title: `How you redeem ${label}`,
    rules,
    exclusions: grouped.hasAny
      ? {
          intro: `Some catalog items in this cart are not eligible for redeeming ${label}:`,
          lead: grouped.items[0]?.name ?? "",
          expandLabel: "View all ineligible items",
          groups: [
            toGroup("Categories", grouped.categories),
            toGroup("Sub-Categories", grouped.subCategories),
            toGroup("Items", grouped.items),
          ].filter((group): group is ExclusionGroup => group !== null),
        }
      : undefined,
  };
}

/** A named group, or `null` when the programme excludes nothing of that kind. */
function toGroup(title: string, entries: ProgramExclusion[]): ExclusionGroup | null {
  if (!entries.length) return null;
  return {
    title,
    names: `${entries
      .map((entry) => entry?.name)
      .filter(Boolean)
      .join(", ")}.`,
  };
}
